/**
 * Campana de notificaciones de pedidos de láminas (cabecera de la SPA).
 *
 * Réplica de la campana del SSR (`templates/base.html`): un botón con un badge de
 * conteo y un panel desplegable con las novedades no vistas, sondeadas en segundo
 * plano. Al abrir el panel se marcan como vistas (como el SSR, que llamaba a
 * `marcar_pedido_notifs_vistas`).
 *
 * Datos vía hooks de TanStack Query sobre `lib/api` (sin `fetch` suelto). El gating
 * de permiso lo decide quien la monta (`AppLayout`): este componente asume que solo
 * se renderiza para quien puede ver pedidos de láminas. Presentacional salvo el
 * estado local del desplegable (abierto/cerrado) y el cierre al hacer clic fuera.
 */
import { useEffect, useRef, useState } from "react";

import { cn } from "@/components/ui";

import { eventStyle, badgeText } from "../format";
import {
  useMarkNotificationsSeen,
  useNotifications,
} from "../hooks/useNotifications";
import type { PedidoNotification } from "../types";

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data } = useNotifications();
  const markSeen = useMarkNotificationsSeen();

  const count = data?.count ?? 0;
  const notifs = data?.notifs ?? [];
  const badge = badgeText(count);

  // Al abrir el panel, marcar como vistas (igual que el SSR). Se dispara una sola
  // vez por apertura; la mutación invalida la query y el badge cae a cero.
  function handleToggle() {
    setOpen((prev) => {
      const next = !prev;
      if (next && count > 0) {
        markSeen.mutate();
      }
      return next;
    });
  }

  // Cierre al hacer clic fuera del componente (comportamiento de dropdown).
  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        aria-label="Notificaciones de pedidos de láminas"
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
      >
        {/* Icono de campana (SVG inline, sin dependencia de iconos). */}
        <svg
          className={cn("h-5 w-5", count > 0 ? "text-emerald-500" : "text-slate-500")}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.8}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
          />
        </svg>
        {badge && (
          <span
            data-testid="notifications-badge"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-emerald-500 px-0.5 text-[9px] font-bold text-white"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Novedades de láminas"
          className="absolute right-0 z-20 mt-2 w-80 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-700">
              Novedades de láminas
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifs.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">
                Sin novedades de láminas
              </p>
            ) : (
              <ul>
                {notifs.map((notif) => (
                  <NotificationItem key={notif.id} notif={notif} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Una fila del panel: chip de evento + título/resumen/actor/fecha (réplica del SSR). */
function NotificationItem({ notif }: { notif: PedidoNotification }) {
  const style = eventStyle(notif.evento);
  return (
    <li className="border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50">
      <div className="flex items-start gap-2">
        <span
          className={cn(
            "mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px]",
            style.chip,
          )}
          aria-hidden="true"
        >
          {/* Punto de color: el detalle del icono lo da el chip; el texto del título
              ya describe el evento. */}
          ●
        </span>
        <div className="min-w-0">
          <p className="mb-0 text-xs font-bold text-slate-600">{notif.titulo}</p>
          <p className="mb-0 truncate text-sm font-semibold text-slate-800">
            {notif.resumen}
          </p>
          <p className="mb-0 text-xs text-slate-500">{notif.actor}</p>
          <p className="mb-0 text-[10px] text-slate-400">{notif.created_at}</p>
        </div>
      </div>
    </li>
  );
}
